<template>
  <div>
    <div onContextMenu="return(false)" id="kwc__bu__tabulator-fields" ref="table"></div>
    <button
      type="button"
      class="controls__button controls__button_add_row"
      @click="addRow"
    >
      <base-material-design-icon icon="plus" class="controls__icon" />
      <span>Add Row</span>
    </button>
    <base-material-design-icon style="font-size:14px;" ref="icon" icon="closeCircle" />
  </div>
</template>

<script>

import Tabulator from 'tabulator-tables'
import BaseModalMixins from '@/common/BaseModalMixins'
import customEditor from './customTabulatorEditor'
import { mapActions, mapState } from 'vuex'

const valuePlaceholder = '- value -'

export default {
  name: 'BulkUpdateFields',
  mixins: [BaseModalMixins],
  props: {
    urlOpts: Array,
    msvLabels: Array,
    showDifficultyScore: Boolean
  },
  data () {
    const sortedDefaultFieldLabels = [
      { 'mappedUrl': 'Mapped URL' },
      { 'relScore': 'Rel Score' }
    ]
    this.msvLabels.forEach(msv => {
      sortedDefaultFieldLabels.push({ [msv.field]: msv.title.toUpperCase() + ' MSV' })
    })
    if (this.showDifficultyScore) {
      sortedDefaultFieldLabels.push({ difficultyScore: 'Difficulty Score' })
    }
    return {
      table: null,
      rowId: 0,
      rawIconHtmlString: '',
      fieldLabels: sortedDefaultFieldLabels.reduce((acc, cur) => {
        const key = Object.keys(cur)[0]
        acc[key] = cur[key]
        return acc
      }, {}),
      fields: sortedDefaultFieldLabels.map(obj => {
        const key = Object.keys(obj)[0]
        return {
          label: obj[key],
          value: key
        }
      })
    }
  },
  computed: {
    ...mapState('keywordCensus', [
      'lastSelectedBulkUpdateField'
    ]),
    values () {
      return {
        mappedUrl: this.urlOpts.reduce((acc, cur) => {
          acc[cur.value] = cur.label
          return acc
        }, {}),
        relScore: [0, 1, 2, 3, 4, 5]
      }
    }
  },
  methods: {
    ...mapActions('keywordCensus', [
      'changeLastSelectedBulkUpdateField'
    ]),
    updateFields () {
      let data = this.table.getData()
      data = data.filter(row => row.field && (row.value === 0 || row.value) && row.value !== valuePlaceholder)
      data = data.map(row => ({ field: row.field, value: row.value }))
      if (data && data.length) {
        this.$emit('fieldData', data)
      }
    },
    addRow () {
      this.rowId += 1
      this.table.addRow({ id: this.rowId, close_button: 1, field: this.lastSelectedBulkUpdateField, value: valuePlaceholder })
      this.table.scrollToRow(this.rowId)
    },
    objectToSelectList (obj) {
      const selectList = Object.keys(obj)
        .filter(key => key !== '-1')
        .map(key => ({
          label: obj[key],
          value: key
        })).sort((a, b) => (a.label?.toLowerCase() > b.label?.toLowerCase()) ? 1 : -1)
      if (-1 in obj) {
        selectList.unshift({
          label: obj[-1],
          value: -1
        })
      }
      return selectList
    }
  },
  mounted () {
    this.rawIconHtmlString = this.$refs.icon.$el.outerHTML.toString()
    this.$refs.icon.$el.parentNode.removeChild(this.$refs.icon.$el)

    const vueComponent = this
    this.table = new Tabulator(this.$refs.table, {
      data: [{ field: vueComponent.lastSelectedBulkUpdateField, value: valuePlaceholder }],
      layout: 'fitColumns',
      movableColumns: false,
      headerVisible: false,
      cellVertAlign: 'middle',
      maxHeight: 170,
      columns: [
        {
          title: '',
          field: 'close_button',
          hozAlign: 'center',
          resizable: false,
          headerSort: false,
          minWidth: 50,
          width: 50,
          formatter: function (cell, formatterParams, onRendered) { // plain text value
            return vueComponent.rawIconHtmlString // this provides material-design-icon as raw html
          },
          cellClick: function (e, cell) {
            const rowCount = cell.getTable().getDataCount()
            const row = cell.getRow()
            if (rowCount === 1) {
              row.getCell('value').setValue(valuePlaceholder)
            } else {
              row.delete()
            }
          }
        },
        {
          title: 'Field',
          field: 'field',
          resizable: false,
          width: 150,
          editor: 'select',
          editorParams: {
            showListOnEmpty: true,
            values: vueComponent.fields
          },
          formatter (cell) {
            const value = cell.getValue()
            return vueComponent.fieldLabels[value] || vueComponent.lastSelectedBulkUpdateField
          },
          cellEdited (cell) {
            cell.getRow().getCell('value').setValue(valuePlaceholder)
            vueComponent.changeLastSelectedBulkUpdateField(cell.getValue())
          }
        },
        {
          title: 'Value',
          field: 'value',
          resizable: false,
          headerSort: false,
          minWidth: 100,
          cellEditing (cell) {
            const field = cell.getRow().getCell('field').getValue()
            if (cell.getValue() === valuePlaceholder || field === 'relScore') {
              cell.setValue('')
            }
          },
          editor: customEditor,
          editorParams (cell) {
            const editorParams = {
              showListOnEmpty: true,
              table: vueComponent.table
            }
            const field = cell.getRow().getCell('field').getValue()
            switch (field) {
              case 'relScore':
                editorParams.editor = 'select'
                editorParams.values = vueComponent.values[field]
                break
              case 'mappedUrl':
                editorParams.editor = 'autocomplete'
                editorParams.freetext = true
                editorParams.values = vueComponent.objectToSelectList(vueComponent.values[field])
                break
              default:
                editorParams.editor = 'number'
                editorParams.min = 0
            }

            return editorParams
          },
          mutator (value) {
            return Array.isArray(value) ? value[0] : value
          },
          formatter (cell) {
            const field = cell.getRow().getCell('field')
            const value = cell.getValue()
            if (value !== 0 && !value) {
              return valuePlaceholder
            }
            if (field) {
              const fieldValue = field.getValue()
              if (fieldValue === 'mappedUrl') {
                cell.getElement().setAttribute('private-content', true)
                if (vueComponent.values[fieldValue]) {
                  return vueComponent.values[fieldValue][value] || value
                }
              } else {
                return Array.isArray(value) ? value[0] : value
              }
            }
            return valuePlaceholder
          }
        }
      ]

    })
  }
}

</script>

<style>
#kwc__bu__tabulator-fields .tabulator-cell {
  height: 40px;
}
</style>

<style scoped lang="scss">

  .controls {
    display: flex;
    margin-top: 30px;

    &__icon {
      margin-right: 2px;
      font-size: 15px;
    }

    &__button {
      height: 40px;
      width: 15%;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      border: none;
      outline: none;
      cursor: pointer;

      color: $rfx-dark-march;
      background-color: #fff;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;
      }

      &_add_row {
        width: 100%;
      }

      &_cancel {
        margin-left: auto;
        margin-right: 10px;
      }
    }
  }

</style>
